import type { DisciplineActivationDetails, DisciplineDemoFeedback } from './interfaces'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

export interface DisciplineReportItem {
  label: string
  value: string
}

export interface DisciplineReportGoalsRow {
  id: string
  title: string
  value: string
  linkUrl?: string
}

export interface DisciplineDemoDetails {
  main: DisciplineReportItem[]
  general: DisciplineReportItem[]
  common: DisciplineReportItem[]
}

interface DisciplineGoalsLabels {
  mainGoal: Record<string, string>
  advancedGoal: Record<string, string>
  focusSkill: Record<string, string>
  gradeLevel: Record<string, string>
  homeschoolInvolvement: Record<string, string>
  gradesMatter: Record<string, string>
  learningNeed: Record<string, string>
  childName: string
}

const RECOMMEND_PREFIX = 'Based on the test results and my observations, I strongly recommend'
const LIST_SEPARATOR = ', '

const text = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined) return null
  return String(value).trim() || null
}

const joined = (values: string[] | null | undefined): string | null =>
  values?.length ? values.join(LIST_SEPARATOR) : null

const recommendation = (value: string | null | undefined): string | null =>
  text(value) ? `${RECOMMEND_PREFIX} ${value}` : null

/** The legacy report hid every row without a value, so the lists keep only the answered ones. */
const items = (entries: [string, string | number | null | undefined][]): DisciplineReportItem[] =>
  entries
    .map(([label, value]) => ({ label, value: text(value) }))
    .filter((item): item is DisciplineReportItem => item.value !== null)

export const buildDemoParentInfo = (feedback: DisciplineDemoFeedback | null): DisciplineReportItem[] => items([
  ['Parent name', feedback?.survey?.customer_name],
  ['Parent presence', feedback?.parent_presence],
])

export const buildDemoKidInfo = (
  feedback: DisciplineDemoFeedback | null,
  labels: { genders: Record<string, string>, grades: Record<string, string> },
): DisciplineReportItem[] => {
  const gender = text(feedback?.survey?.gender)
  const grade = text(feedback?.grade)

  return items([
    ['Student name', feedback?.survey?.kid_name],
    ['Kids gender', gender ? labels.genders[gender] ?? gender : null],
    ['Kids grade', grade ? labels.grades[grade] ?? grade : null],
    ['Study goal', feedback?.study_goal],
  ])
}

/** The demo form differs per discipline: Math rates engagement, ELA rates the four skills. */
export const buildDemoDetails = (code: string, feedback: DisciplineDemoFeedback | null): DisciplineDemoDetails => {
  const common: [string, string | number | null | undefined][] = [
    ['Student\'s strong sides', feedback?.survey?.strong_sides],
    ['Student’s areas of growth', feedback?.survey?.weak_sides],
    ['Comment for parent advisor', feedback?.comment_parent_advisor],
  ]

  if (code === Discipline.ELA) {
    return {
      main: items([
        ['Speaking', feedback?.speaking],
        ['Writing', feedback?.writing],
        ['Reading', feedback?.reading],
        ['Grammar', feedback?.grammar],
      ]),
      general: items([
        ['What grade level is recommended for starting learning?', feedback?.recommended_grade_level],
        ['Positive learning trait', feedback?.positive_learning_trait],
        ['Strengths', joined(feedback?.strength)],
        ['Areas for Improvement', joined(feedback?.improvement)],
        ['Complete the sentence', recommendation(feedback?.tutor_recommend)],
      ]),
      common: items(common),
    }
  }

  return {
    main: items([
      ['Engagement rate', feedback?.engagement_rate],
      ['Comprehension rate', feedback?.comprehension_rate],
    ]),
    general: items([
      ['Pre-made feedback on this kid', feedback?.score],
      ['Your demo lesson’s topic', feedback?.topic],
      ['Covered topics', joined(feedback?.covered_topic)],
      ['Topics to cover', joined(feedback?.topic_to_cover)],
      ['Positive learning trait', feedback?.positive_learning_trait],
      ['Complete the sentence', recommendation(feedback?.tutor_recommend)],
    ]),
    common: items([
      ['What grade level is recommended for starting learning?', feedback?.recommended_grade_level],
      ...common,
    ]),
  }
}

const label = (labels: Record<string, string>, code: string | null | undefined): string | null => {
  const key = text(code)
  return key ? labels[key] ?? key : null
}

const row = (id: string, title: string, value: string | null): DisciplineReportGoalsRow | null =>
  value ? { id, title, value } : null

/** Port of the legacy goals card: shown only when the activation goals flow left an answer. */
export const buildDisciplineGoalsRows = (
  details: DisciplineActivationDetails | null,
  labels: DisciplineGoalsLabels,
): DisciplineReportGoalsRow[] => {
  if (!details) return []

  const mainGoalCode = text(details.expectations?.[0])
  const priority = mainGoalCode ? text(labels.mainGoal[mainGoalCode]) : null
  const answers = [
    details.focus_skill, details.current_grade, details.target_grade, details.advanced_goal,
    details.test_name, details.homeschool_involvement, details.homeschool_vision, details.grades_matter,
    details.curriculum_note, details.learning_needs?.join(''), details.curriculum_file?.filename,
  ]

  if (priority === null && !answers.some(answer => text(answer))) return []

  const learningNeeds = text((details.learning_needs ?? [])
    .map(need => label(labels.learningNeed, need))
    .filter(Boolean)
    .join(LIST_SEPARATOR))
  const file = details.curriculum_file
  const fileRow: DisciplineReportGoalsRow | null = file?.view_link && file.filename
    ? { id: 'curriculum-file', title: 'Curriculum file', value: file.filename, linkUrl: file.view_link }
    : null

  return [
    row('priority', 'Priority', priority),
    row('advanced-goal', 'Working toward', label(labels.advancedGoal, details.advanced_goal)),
    row('focus-skill', 'Main focus', label(labels.focusSkill, details.focus_skill)),
    row('current-grade', 'Current grade', label(labels.gradeLevel, details.current_grade)),
    row('target-grade', 'Target grade', label(labels.gradeLevel, details.target_grade)),
    row('test-name', 'Test', text(details.test_name)),
    row('homeschool-involvement', 'Tutor involvement', label(labels.homeschoolInvolvement, details.homeschool_involvement)),
    row('homeschool-vision', 'Vision', text(details.homeschool_vision)),
    row('grades-matter', 'Grades priority', label(labels.gradesMatter, details.grades_matter)),
    row('learning-needs', 'Learning needs', learningNeeds),
    row('child-note', `Details about ${text(labels.childName) ?? 'the kid'}`, text(details.preferences)),
    row('curriculum-note', 'Curriculum preferences', text(details.curriculum_note)),
    fileRow,
  ].filter((entry): entry is DisciplineReportGoalsRow => entry !== null)
}
